import { lazy } from 'react';
import {
  IconArrowsExchange,
  IconBook,
  IconBuildingBank,
  IconCalendarStats,
  IconCash,
  IconChartBar,
  IconChartPie,
  IconChecklist,
  IconCoins,
  IconFileInvoice,
  IconFileSpreadsheet,
  IconGauge,
  IconHierarchy2,
  IconLayoutDashboard,
  IconListNumbers,
  IconReceipt,
  IconReceiptTax,
  IconRepeat,
  IconReportMoney,
  IconScale,
  IconShieldCheck,
  IconTargetArrow,
  IconTimeline,
  IconTools,
  IconTruckDelivery,
  IconUsersGroup,
  IconWallet,
  IconWorld,
} from '@tabler/icons-react';
import { defineModule } from '../types';
import { indexRoute, lazyRoute, routeGroup } from '../routing';
import { P } from './perms';

const FinSnapshotWidget = lazy(() => import('./widgets').then((m) => ({ default: m.FinSnapshotWidget })));

const journals = () => import('./pages/JournalsPages');
const ledger = () => import('./pages/LedgerPages');
const close = () => import('./pages/ClosePages');
const tools = () => import('./pages/ToolsPages');
const arap = () => import('./pages/ArApPages');
const receipts = () => import('./pages/ReceiptsPages');
const payments = () => import('./pages/PaymentsPages');
const expenses = () => import('./pages/ExpensePages');
const bank = () => import('./pages/BankPages');
const recon = () => import('./pages/ReconciliationPages');
const custody = () => import('./pages/CustodyPages');
const assets = () => import('./pages/AssetsPages');
const budgets = () => import('./pages/BudgetsPages');
const overview = () => import('./pages/OverviewPages');

/**
 * FIN — Finance & accounting / المالية والمحاسبة (FR-FIN-01..17) of an IT services company.
 * GL & journals (SoD-7), customers & collections, expenses & suppliers (input VAT → purchase
 * register), payments (SoD-2/3), bank & cash, reconciliation, staff custody, fixed assets,
 * budgets, periods & close, statements, partners' equity, reports.
 */
export default defineModule({
  code: 'fin',
  nameAr: 'المالية',
  nameEn: 'Finance',
  icon: IconReportMoney,
  order: 90,
  menu: [
    { key: 'fin.overview', labelKey: 'fin:nav.overview', path: '/fin', icon: IconLayoutDashboard, permission: P.dashboard.read },
    {
      key: 'fin.gl',
      labelKey: 'fin:nav.gl',
      icon: IconBook,
      children: [
        { key: 'fin.journals', labelKey: 'fin:nav.journals', path: '/fin/journals', icon: IconListNumbers, permission: P.journal.read },
        { key: 'fin.recurring', labelKey: 'fin:nav.recurring', path: '/fin/recurring', icon: IconRepeat, permission: P.journal.read },
        { key: 'fin.accounts', labelKey: 'fin:nav.accounts', path: '/fin/accounts', icon: IconHierarchy2, permission: P.account.read },
        { key: 'fin.tb', labelKey: 'fin:nav.trialBalance', path: '/fin/trial-balance', icon: IconScale, permission: [P.account.read, P.report.read] },
        { key: 'fin.ledger', labelKey: 'fin:nav.ledger', path: '/fin/ledger', icon: IconBook, permission: P.journal.read },
      ],
    },
    {
      key: 'fin.arap',
      labelKey: 'fin:nav.arap',
      icon: IconReceipt,
      children: [
        { key: 'fin.ar', labelKey: 'fin:nav.ar', path: '/fin/ar', icon: IconReceipt, permission: P.ar.read },
        { key: 'fin.receipts', labelKey: 'fin:nav.receipts', path: '/fin/receipts', icon: IconCash, permission: P.receipt.read },
      ],
    },
    {
      key: 'fin.suppliers',
      labelKey: 'fin:nav.suppliers',
      icon: IconWallet,
      children: [
        { key: 'fin.expenses', labelKey: 'fin:nav.expenses', path: '/fin/expense-invoices', icon: IconReceiptTax, permission: P.ap.read },
        { key: 'fin.ap', labelKey: 'fin:nav.ap', path: '/fin/ap', icon: IconFileInvoice, permission: P.ap.read },
        { key: 'fin.payments', labelKey: 'fin:nav.payments', path: '/fin/payments', icon: IconTruckDelivery, permission: P.payment.read },
        { key: 'fin.orders', labelKey: 'fin:nav.paymentOrders', path: '/fin/payment-orders', icon: IconChecklist, permission: P.payment.read },
        { key: 'fin.schedule', labelKey: 'fin:nav.paymentSchedule', path: '/fin/payment-schedule', icon: IconCalendarStats, permission: P.ap.read },
      ],
    },
    {
      key: 'fin.treasury',
      labelKey: 'fin:nav.treasury',
      icon: IconBuildingBank,
      children: [
        { key: 'fin.banks', labelKey: 'fin:nav.bankAccounts', path: '/fin/bank-accounts', icon: IconBuildingBank, permission: P.bank.read },
        { key: 'fin.transfers', labelKey: 'fin:nav.transfers', path: '/fin/transfers', icon: IconArrowsExchange, permission: P.bank.read },
        { key: 'fin.cheques', labelKey: 'fin:nav.cheques', path: '/fin/cheques', icon: IconReceipt, permission: P.cheque.read },
        { key: 'fin.recon', labelKey: 'fin:nav.reconciliation', path: '/fin/reconciliation', icon: IconShieldCheck, permission: P.recon.read },
        { key: 'fin.custody', labelKey: 'fin:nav.custody', path: '/fin/custody', icon: IconUsersGroup, permission: P.custody.read },
        { key: 'fin.forecast', labelKey: 'fin:nav.cashForecast', path: '/fin/cash-forecast', icon: IconTimeline, permission: P.report.read },
      ],
    },
    {
      key: 'fin.assetsBudgets',
      labelKey: 'fin:nav.assetsBudgets',
      icon: IconCoins,
      children: [
        { key: 'fin.assets', labelKey: 'fin:nav.assets', path: '/fin/assets', icon: IconCoins, permission: P.asset.read },
        { key: 'fin.depreciation', labelKey: 'fin:nav.depreciation', path: '/fin/depreciation', icon: IconChartBar, permission: P.asset.read },
        { key: 'fin.budgets', labelKey: 'fin:nav.budgets', path: '/fin/budgets', icon: IconTargetArrow, permission: P.budget.read },
        { key: 'fin.bva', labelKey: 'fin:nav.budgetVsActual', path: '/fin/budget-vs-actual', icon: IconGauge, permission: P.budget.read },
      ],
    },
    {
      key: 'fin.closeReporting',
      labelKey: 'fin:nav.closeReporting',
      icon: IconChecklist,
      children: [
        { key: 'fin.periods', labelKey: 'fin:nav.periods', path: '/fin/periods', icon: IconCalendarStats, permission: P.period.read },
        { key: 'fin.close', labelKey: 'fin:nav.close', path: '/fin/close', icon: IconChecklist, permission: P.close.read },
        { key: 'fin.fx', labelKey: 'fin:nav.fxRevaluation', path: '/fin/fx-revaluation', icon: IconWorld, permission: P.journal.read },
        { key: 'fin.ledgerChecks', labelKey: 'fin:nav.ledgerChecks', path: '/fin/ledger-checks', icon: IconTools, permission: P.ledgerCheck.read },
        { key: 'fin.statements', labelKey: 'fin:nav.statements', path: '/fin/statements/BS', icon: IconFileSpreadsheet, permission: P.statement.read },
        { key: 'fin.equity', labelKey: 'fin:nav.equity', path: '/fin/equity', icon: IconChartPie, permission: P.equity.read },
        { key: 'fin.reports', labelKey: 'fin:nav.reports', path: '/fin/reports', icon: IconReportMoney, permission: P.report.read },
      ],
    },
  ],
  routes: {
    admin: [
      lazyRoute('fin', () => overview().then((m) => ({ default: m.FinOverviewPage })), { permission: P.dashboard.read, crumb: 'fin:nav.overview' }),
      routeGroup('fin/journals', 'fin:nav.journals', [
        indexRoute(() => journals().then((m) => ({ default: m.JournalsPage })), P.journal.read),
        lazyRoute(':id', () => journals().then((m) => ({ default: m.JournalEditorPage })), { permission: P.journal.read, crumb: 'fin:journals.entry' }),
      ]),
      lazyRoute('fin/recurring', () => tools().then((m) => ({ default: m.RecurringPage })), { permission: P.journal.read, crumb: 'fin:nav.recurring' }),
      lazyRoute('fin/accounts', () => import('./pages/AccountsPage'), { permission: P.account.read, crumb: 'fin:nav.accounts' }),
      lazyRoute('fin/trial-balance', () => ledger().then((m) => ({ default: m.TrialBalancePage })), { permission: [P.account.read, P.report.read], crumb: 'fin:nav.trialBalance' }),
      lazyRoute('fin/ledger', () => ledger().then((m) => ({ default: m.GeneralLedgerPage })), { permission: P.journal.read, crumb: 'fin:nav.ledger' }),
      lazyRoute('fin/ar', () => arap().then((m) => ({ default: m.ArWorkspacePage })), { permission: P.ar.read, crumb: 'fin:nav.ar' }),
      lazyRoute('fin/ap', () => arap().then((m) => ({ default: m.ApWorkspacePage })), { permission: P.ap.read, crumb: 'fin:nav.ap' }),
      routeGroup('fin/receipts', 'fin:nav.receipts', [
        indexRoute(() => receipts().then((m) => ({ default: m.ReceiptsPage })), P.receipt.read),
        lazyRoute(':id', () => receipts().then((m) => ({ default: m.ReceiptPage })), { permission: P.receipt.read, crumb: 'fin:receipts.entity' }),
      ]),
      routeGroup('fin/payments', 'fin:nav.payments', [
        indexRoute(() => payments().then((m) => ({ default: m.PaymentsPage })), P.payment.read),
        lazyRoute(':id', () => payments().then((m) => ({ default: m.PaymentPage })), { permission: P.payment.read, crumb: 'fin:payments.entity' }),
      ]),
      routeGroup('fin/payment-orders', 'fin:nav.paymentOrders', [
        indexRoute(() => payments().then((m) => ({ default: m.PaymentOrdersPage })), P.payment.read),
        lazyRoute(':id', () => payments().then((m) => ({ default: m.PaymentOrderPage })), { permission: P.payment.read, crumb: 'fin:orders.entity' }),
      ]),
      lazyRoute('fin/payment-schedule', () => payments().then((m) => ({ default: m.PaymentSchedulePage })), { permission: P.ap.read, crumb: 'fin:nav.paymentSchedule' }),
      routeGroup('fin/expense-invoices', 'fin:nav.expenses', [
        indexRoute(() => expenses().then((m) => ({ default: m.ExpenseInvoicesPage })), P.ap.read),
        lazyRoute(':id', () => expenses().then((m) => ({ default: m.ExpenseInvoicePage })), { permission: P.ap.read, crumb: 'fin:expenses.entity' }),
      ]),
      routeGroup('fin/custody', 'fin:nav.custody', [
        indexRoute(() => custody().then((m) => ({ default: m.CustodyListPage })), P.custody.read),
        lazyRoute(':id', () => custody().then((m) => ({ default: m.CustodyPage })), { permission: P.custody.read, crumb: 'fin:custody.entity' }),
      ]),
      lazyRoute('fin/bank-accounts', () => bank().then((m) => ({ default: m.BankAccountsPage })), { permission: P.bank.read, crumb: 'fin:nav.bankAccounts' }),
      lazyRoute('fin/transfers', () => bank().then((m) => ({ default: m.TransfersPage })), { permission: P.bank.read, crumb: 'fin:nav.transfers' }),
      lazyRoute('fin/cheques', () => bank().then((m) => ({ default: m.ChequesPage })), { permission: P.cheque.read, crumb: 'fin:nav.cheques' }),
      routeGroup('fin/reconciliation', 'fin:nav.reconciliation', [
        indexRoute(() => recon().then((m) => ({ default: m.ReconciliationPage })), P.recon.read),
        lazyRoute(':id', () => recon().then((m) => ({ default: m.StatementMatchPage })), { permission: P.recon.read, crumb: 'fin:recon.matchTitle' }),
      ]),
      lazyRoute('fin/cash-forecast', () => overview().then((m) => ({ default: m.CashForecastPage })), { permission: P.report.read, crumb: 'fin:nav.cashForecast' }),
      routeGroup('fin/assets', 'fin:nav.assets', [
        indexRoute(() => assets().then((m) => ({ default: m.AssetsPage })), P.asset.read),
        lazyRoute(':id', () => assets().then((m) => ({ default: m.AssetPage })), { permission: P.asset.read, crumb: 'fin:assets.entity' }),
      ]),
      lazyRoute('fin/depreciation', () => assets().then((m) => ({ default: m.DepreciationPage })), { permission: P.asset.read, crumb: 'fin:nav.depreciation' }),
      routeGroup('fin/budgets', 'fin:nav.budgets', [
        indexRoute(() => budgets().then((m) => ({ default: m.BudgetsPage })), P.budget.read),
        lazyRoute(':id', () => budgets().then((m) => ({ default: m.BudgetPage })), { permission: P.budget.read, crumb: 'fin:budgets.entity' }),
      ]),
      lazyRoute('fin/budget-vs-actual', () => budgets().then((m) => ({ default: m.BudgetVsActualPage })), { permission: P.budget.read, crumb: 'fin:nav.budgetVsActual' }),
      lazyRoute('fin/periods', () => import('./pages/PeriodsPage'), { permission: P.period.read, crumb: 'fin:nav.periods' }),
      routeGroup('fin/close', 'fin:nav.close', [
        indexRoute(() => close().then((m) => ({ default: m.CloseListPage })), P.close.read),
        lazyRoute(':periodId', () => close().then((m) => ({ default: m.CloseChecklistPage })), { permission: P.close.read, crumb: 'fin:close.checklist' }),
      ]),
      lazyRoute('fin/fx-revaluation', () => tools().then((m) => ({ default: m.FxRevaluationPage })), { permission: P.journal.read, crumb: 'fin:nav.fxRevaluation' }),
      lazyRoute('fin/ledger-checks', () => tools().then((m) => ({ default: m.LedgerChecksPage })), { permission: P.ledgerCheck.read, crumb: 'fin:nav.ledgerChecks' }),
      routeGroup('fin/statements', 'fin:nav.statements', [
        indexRoute(() => import('./pages/StatementsPages'), P.statement.read),
        lazyRoute(':code', () => import('./pages/StatementsPages'), { permission: P.statement.read }),
      ]),
      lazyRoute('fin/equity', () => import('./pages/EquityPage'), { permission: P.equity.read, crumb: 'fin:nav.equity' }),
      lazyRoute('fin/reports', () => import('./pages/ReportsPage'), { permission: P.report.read, crumb: 'fin:nav.reports' }),
    ],
  },
  dashboards: [{ key: 'fin.snapshot', titleKey: 'fin:widgets.snapshot', component: FinSnapshotWidget, permission: P.dashboard.read, order: 20, span: { base: 12, md: 6, lg: 4 } }],
});
